// src/GlobalComponents/ContactFormModal.js
import React, { useState } from "react";

const ContactFormModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // Gradient matching the site theme
  const GRADIENT_PRIMARY = 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)';

  // API endpoint – adjust if your actual endpoint differs
  const API_URL = process.env.NODE_ENV === 'development'
    ? 'http://127.0.0.1:8000/api/contact/'   // local Django dev server
    : 'https://www.zwiko.co.za/api/contact/'; // production

  // Helper to get CSRF token from cookies (Django standard)
  const getCookie = (name) => {
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
      const cookies = document.cookie.split(';');
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        if (cookie.substring(0, name.length + 1) === (name + '=')) {
          cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
          break;
        }
      }
    }
    return cookieValue;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (submitStatus) setSubmitStatus(null); // clear old status on new input
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // Basic validation
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      setSubmitStatus({ type: 'error', message: 'Please fill in all fields.' });
      setIsSubmitting(false);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setSubmitStatus({ type: 'error', message: 'Please enter a valid email address.' });
      setIsSubmitting(false);
      return;
    }

    try {
      const csrfToken = getCookie('csrftoken');
      const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
        credentials: 'include', // important for sending cookies (CSRF)
      };

      if (csrfToken) {
        requestOptions.headers['X-CSRFToken'] = csrfToken;
      }

      const response = await fetch(API_URL, requestOptions);

      if (response.ok) {
        setSubmitStatus({
          type: 'success',
          message: 'Thank you for your message! We will get back to you within 24 hours.'
        });
        setFormData({ name: '', email: '', message: '' }); // clear form
      } else {
        const errorData = await response.json().catch(() => ({}));
        let errorMessage = 'There was an error sending your message. Please try again.';

        if (response.status === 403) errorMessage = 'CSRF verification failed. Please refresh the page and try again.';
        else if (response.status === 400) errorMessage = 'Please check your input and try again.';
        else if (response.status === 500) errorMessage = 'Server error. Please try again later.';

        setSubmitStatus({ type: 'error', message: errorMessage });
      }
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: 'There was a network error. Please check your connection and try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const styles = {
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      backdropFilter: 'blur(5px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
    modalContent: {
      background: 'white',
      borderRadius: '16px',
      padding: '2rem',
      maxWidth: '450px',
      width: '90%',
      boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
      position: 'relative',
      fontFamily: "'Inter', sans-serif",
    },
    modalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1.5rem',
    },
    modalTitle: {
      fontSize: '1.5rem',
      fontWeight: 700,
      background: GRADIENT_PRIMARY,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      margin: 0,
    },
    closeButton: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      fontSize: '1.5rem',
      color: '#6c757d',
      padding: '4px 8px',
      borderRadius: '8px',
      transition: 'all 0.2s',
    },
    formGroup: {
      marginBottom: '1.2rem',
    },
    label: {
      display: 'block',
      marginBottom: '0.4rem',
      fontWeight: 600,
      color: '#2c3e50',
      fontSize: '0.9rem',
    },
    input: {
      width: '100%',
      padding: '12px',
      border: '1px solid #e9ecef',
      borderRadius: '8px',
      fontSize: '1rem',
      outline: 'none',
      transition: 'border-color 0.2s',
      fontFamily: 'inherit',
    },
    textarea: {
      width: '100%',
      padding: '12px',
      border: '1px solid #e9ecef',
      borderRadius: '8px',
      fontSize: '1rem',
      outline: 'none',
      transition: 'border-color 0.2s',
      minHeight: '100px',
      resize: 'vertical',
      fontFamily: 'inherit',
    },
    submitButton: {
      background: GRADIENT_PRIMARY,
      color: 'white',
      border: 'none',
      padding: '12px 24px',
      borderRadius: '8px',
      fontWeight: 600,
      fontSize: '1rem',
      cursor: 'pointer',
      width: '100%',
      transition: 'opacity 0.2s, transform 0.2s',
      marginTop: '0.5rem',
      opacity: isSubmitting ? 0.7 : 1,
      pointerEvents: isSubmitting ? 'none' : 'auto',
    },
    statusMessage: {
      padding: '12px',
      borderRadius: '8px',
      textAlign: 'center',
      fontSize: '0.9rem',
      fontWeight: '500',
      marginTop: '1rem',
      background: submitStatus?.type === 'success' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
      color: submitStatus?.type === 'success' ? '#16a34a' : '#dc2626',
      border: `1px solid ${submitStatus?.type === 'success' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)'}`,
    },
    footerNote: {
      textAlign: 'center',
      marginTop: '1rem',
      color: '#6c757d',
      fontSize: '0.85rem',
    },
  };

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>Contact Us</h3>
          <button style={styles.closeButton} onClick={onClose} aria-label="Close form">✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="name">Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              style={styles.input}
              placeholder="Your name"
              disabled={isSubmitting}
              required
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              style={styles.input}
              placeholder="your.email@example.com"
              disabled={isSubmitting}
              required
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="message">Message</label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              style={styles.textarea}
              placeholder="How can we help you?"
              disabled={isSubmitting}
              required
            />
          </div>
          <button type="submit" style={styles.submitButton} disabled={isSubmitting}>
            {isSubmitting ? "Sending..." : "Send Message"}
          </button>
        </form>

        {submitStatus && (
          <div style={styles.statusMessage}>
            {submitStatus.message}
          </div>
        )}

        <p style={styles.footerNote}>
          We'll get back to you soon!
        </p>
      </div>
    </div>
  );
};

export default ContactFormModal;